import {
  addDays,
  format,
  isAfter,
  isBefore,
  isValid,
  parse,
  startOfDay,
} from "date-fns";

function parseCalendarDate(value: string | null): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) && format(parsed, "yyyy-MM-dd") === value
    ? parsed
    : null;
}

function positiveInteger(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
}

export type FavoriteHotelStayContext = {
  checkin: string;
  checkout: string;
  numberOfAdults: number;
  childrenAges: number[];
};

export type FavoriteHotelStayAuthority = {
  checkin?: string;
  checkout?: string;
  numberOfAdults: number;
  childrenAges: number[];
};

export function favoriteHotelStayAuthorityParams(
  authority: FavoriteHotelStayAuthority
): URLSearchParams {
  const params = new URLSearchParams();
  if (authority.checkin && authority.checkout) {
    params.set("checkin", authority.checkin);
    params.set("checkout", authority.checkout);
  }
  params.set("numberOfAdults", String(authority.numberOfAdults));
  params.set("childrenAges", authority.childrenAges.join(","));
  return params;
}

export function getFavoriteHotelStayContext(
  activeParams: URLSearchParams,
  now = new Date()
): FavoriteHotelStayContext {
  const activeCheckin = activeParams.get("checkin");
  const activeCheckout = activeParams.get("checkout");
  const parsedCheckin = parseCalendarDate(activeCheckin);
  const parsedCheckout = parseCalendarDate(activeCheckout);
  const hasActiveDates = Boolean(
    parsedCheckin &&
      parsedCheckout &&
      !isBefore(parsedCheckin, startOfDay(now)) &&
      isAfter(parsedCheckout, parsedCheckin)
  );

  return {
    checkin:
      hasActiveDates && activeCheckin
        ? activeCheckin
        : format(addDays(now, 1), "yyyy-MM-dd"),
    checkout:
      hasActiveDates && activeCheckout
        ? activeCheckout
        : format(addDays(now, 2), "yyyy-MM-dd"),
    numberOfAdults: positiveInteger(activeParams.get("numberOfAdults"), 2),
    childrenAges: (activeParams.get("childrenAges") ?? "")
      .split(",")
      .map((segment) => segment.trim())
      .filter(Boolean)
      .map(Number)
      .filter((age) => Number.isSafeInteger(age) && age >= 0 && age <= 17),
  };
}

function favoriteHotelStayParams(
  activeParams: URLSearchParams,
  now = new Date()
): URLSearchParams {
  const stay = getFavoriteHotelStayContext(activeParams, now);

  return new URLSearchParams({
    checkin: stay.checkin,
    checkout: stay.checkout,
    numberOfAdults: String(stay.numberOfAdults),
    childrenAges: stay.childrenAges.join(","),
  });
}

export function buildFavoritesUrl(
  activeParams: URLSearchParams,
  now = new Date()
): string {
  return `/favorites?${favoriteHotelStayParams(activeParams, now).toString()}`;
}

export function buildFavoriteHotelDetailsUrl(
  propertyId: number,
  activeParams: URLSearchParams,
  now = new Date()
): string {
  const params = favoriteHotelStayParams(activeParams, now);
  return `/hotel/details/${propertyId}?${params.toString()}`;
}
